// AdminModal.tsx
import { Modal, Form, Input, Button, Space, Switch, Divider, Typography } from 'antd';
import { UserOutlined, LockOutlined, ApiOutlined } from '@ant-design/icons';
import { useSuperAdmin } from '../../../contexts/SuperAdminContext';
import { useEffect } from 'react';

interface AdminFormValues {
  username: string;
  password?: string;
  analyticsPassword?: string;
  active: boolean;
  role: string;
  pixelDepositTracking?: boolean;
}

interface AdminModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: {
    id: string;
    username: string;
    analyticsPassword?: string;
    active: boolean;
    role: string;
    pixelDepositTracking?: boolean;
  };
}

export const AdminModal = ({ open, onClose, mode, initialData }: AdminModalProps) => {
  const [form] = Form.useForm<AdminFormValues>();
  const { createAdmin, editAdmin, getAdminConfig } = useSuperAdmin();

  useEffect(() => {
    const loadAdminData = async () => {
      if (mode === 'edit' && initialData) {
        // Cargar configuración actual del admin
        try {
          const config = await getAdminConfig(initialData.id);
          form.setFieldsValue({
            username: initialData.username,
            active: initialData.active,
            role: initialData.role,
            pixelDepositTracking: config.pixelDepositTracking ?? false
          });
        } catch (error) {
          console.error('[AdminModal] Error loading admin config:', error);
          // Usar valores por defecto en caso de error
          form.setFieldsValue({
            username: initialData.username,
            active: initialData.active,
            role: initialData.role,
            pixelDepositTracking: false
          });
        }
      } else {
        form.resetFields();
      }
    };

    if (open) {
      loadAdminData();
    }
  }, [form, initialData, mode, open, getAdminConfig]);

const handleSubmit = (values: AdminFormValues) => {
  if (mode === 'edit' && initialData) {
    // Fix: Pass id and data as separate arguments
    editAdmin(initialData.id, {
      username: values.username,
      ...(values.password && { password: values.password }),
      ...(values.analyticsPassword && { analyticsPassword: values.analyticsPassword }),
      active: values.active,
      role: values.role,
      pixelDepositTracking: values.pixelDepositTracking ?? false
    });
  } else {
    createAdmin({
      username: values.username,
      password: values.password,
      analyticsPassword: values.analyticsPassword,
      active: values.active,
      role: 'ADMIN',
      pixelDepositTracking: values.pixelDepositTracking ?? false
    });
  }
  onClose();
  form.resetFields();
};
  return (
    <Modal
      title={`${mode === 'edit' ? 'Edit' : 'Create'} Admin`}
      open={open}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ active: true, role: 'ADMIN', pixelDepositTracking: false }}
      >
        <Form.Item
          label="Username"
          name="username"
          rules={[{ required: true, message: 'Please input username' }]}
        >
          <Input prefix={<UserOutlined />} />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          rules={[{ required: mode === 'create', message: 'Please input password' }]}
        >
          <Input.Password 
            prefix={<LockOutlined />} 
            placeholder={mode === 'edit' ? "Leave blank to keep current password" : "Enter password"}
          />
        </Form.Item>

        <Form.Item
          label="Analytics Password"
          name="analyticsPassword"
          rules={[{ required: mode === 'create', message: 'Please input analytics password' }]}
        >
          <Input.Password 
            prefix={<LockOutlined />} 
            placeholder={mode === 'edit' ? "Leave blank to keep current password" : "Enter analytics password"}
          />
        </Form.Item>

        <Form.Item
          label="Active"
          name="active"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Divider orientation="left">
          <Typography.Text strong style={{ fontSize: '14px' }}>
            <ApiOutlined style={{ marginRight: '8px', color: '#722ed1' }} />
            Configuración de Webhooks
          </Typography.Text>
        </Divider>

        <Form.Item
          label="TimbaVegas/Facebook Webhook"
          name="pixelDepositTracking"
          valuePropName="checked"
          tooltip="Activa el envío automático de webhooks a TimbaVegas cuando se completan depósitos"
        >
          <Switch 
            checkedChildren="Activado" 
            unCheckedChildren="Desactivado"
          />
        </Form.Item>

        <Form.Item
          label="Role"
          name="role"
          hidden
        >
          <Input />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {mode === 'create' ? 'Create' : 'Update'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};